'use client';

import { useState } from 'react';
import useModal from 'src/components/Modals/hooks/useModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { useDeleteAccount } from 'src/hooks/useDeleteAccount';
import Link from 'src/components/primitives/Link/Link';

const DeleteAccountModal = () => {
	const { hide } = useModal();
	const { deleteAccount, isLoading, hasSubaccounts, canDelete } =
		useDeleteAccount();
	const [confirmText, setConfirmText] = useState('');

	const isConfirmed = confirmText.toLowerCase() === 'delete';

	const handleDelete = async () => {
		const result = await deleteAccount();
		if (result.success) {
			hide();
		}
	};

	return (
		<ResponsiveModal.Container onClose={hide} dataTestId="delete-account-modal">
			<ResponsiveModal.Title closeButtonTestId="delete-account-modal-close">
				Delete Account Data
			</ResponsiveModal.Title>
			<ResponsiveModal.Body className="flex flex-col gap-y-lg">
				{hasSubaccounts ? (
					<InlineAlert
						variant="warning"
						title="Subaccounts must be deleted first"
					>
						You must delete all your Drift subaccounts before you can delete
						your account data. Go to your accounts drawer to delete each
						subaccount.
					</InlineAlert>
				) : (
					<>
						<InlineAlert variant="warning" title="This action cannot be undone">
							Deleting your account will permanently remove your off-chain data
							from Drift&apos;s systems.
						</InlineAlert>

						<div className="flex flex-col gap-y-md rounded-sm border border-stroke-divider bg-input-bg p-lg">
							<p className="typo-label-xs text-text-default">
								What will be deleted:
							</p>
							<ul className="flex flex-col gap-y-xs typo-body-xs text-text-secondary">
								<li>• Analytics and usage data</li>
								<li>• Login credentials and social account associations</li>
								<li>• Device identifiers and location data</li>
								<li>• Any stored preferences</li>
							</ul>
						</div>

						<div className="flex flex-col gap-y-md rounded-sm border border-stroke-divider bg-input-bg p-lg">
							<p className="typo-label-xs text-text-default">
								What cannot be deleted:
							</p>
							<ul className="flex flex-col gap-y-xs typo-body-xs text-text-secondary">
								<li>• On-chain transaction history</li>
								<li>• Historical positions and trades on the blockchain</li>
								<li>• Wallet addresses recorded on-chain</li>
							</ul>
						</div>

						<p className="typo-body-xs text-text-secondary">
							For more information, see our{' '}
							<Link href="/data-policy" variant="primary">
								Data Policy
							</Link>
							.
						</p>

						<div className="flex flex-col gap-y-sm">
							<label className="typo-label-xs text-text-default">
								Type &quot;delete&quot; to confirm:
							</label>
							<input
								type="text"
								value={confirmText}
								onChange={(e) => setConfirmText(e.target.value)}
								placeholder="delete"
								className="focus:border-stroke-active rounded-sm border border-stroke-divider bg-input-bg px-md py-sm typo-body-sm text-text-default placeholder:text-text-tertiary focus:outline-none"
							/>
						</div>
					</>
				)}
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				<Button
					size="lg"
					variant="secondary"
					className="flex-1"
					onClick={hide}
					disabled={isLoading}
					dataTestId="delete-account-modal-cancel"
				>
					Cancel
				</Button>
				<Button
					size="lg"
					variant="negative"
					className="flex-1"
					onClick={handleDelete}
					disabled={!canDelete || !isConfirmed || isLoading}
					isLoading={isLoading}
					dataTestId="delete-account-modal-submit"
				>
					Delete Account
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
};

export default DeleteAccountModal;
